import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Pagination, Stack, Typography } from '@mui/material';
import { Forum, Restaurant } from '@mui/icons-material';
import { getNotificationDestination, openNotificationDestination } from '../../utils/navigation';
import TopAppBar from '../layout/TopAppBar';
import { HeaderTriggerTabs, NotificationCard, PageContainer, SurfaceCard } from '../ui/ConvivialComponents';
import { listNotifications } from '../../services/firebaseData';
import { requestNativeLogin } from '../../services/nativeAppBridge';

export default function Notifications() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('user_id');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState('tables');

  useEffect(() => {
    if (!userId) {
        const handledByNative = requestNativeLogin('/notifications');

        if (!handledByNative) {
            navigate('/login', { state: { redirectTo: '/notifications' } });
        }
    }
  }, [navigate, userId]);

  useEffect(() => {
    if (!userId) return;
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const data = await listNotifications(userId, page);
        setNotifications(data.notifications);
        setTotalPages(data.totalPages);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [userId, page]);

  const visibleNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      const text = `${notification.content?.title || ''} ${notification.content?.body || ''}`.toLowerCase();
      const isRequest = text.includes('richiesta') || text.includes('partecip');
      return activeTab === 'requests' ? isRequest : !isRequest;
    });
  }, [notifications, activeTab]);

  return (
    <>
      <TopAppBar
        title="ConvivialApp.it"
        showAvatar
        titleAction={
          <HeaderTriggerTabs
            value={activeTab}
            options={[
              { id: 'tables', label: 'Tavoli', icon: <Restaurant fontSize="small" /> },
              { id: 'requests', label: 'Richieste', icon: <Forum fontSize="small" /> },
            ]}
            onChange={setActiveTab}
          />
        }
      />
      <PageContainer sx={{ marginTop: 'var(--cv-top-bar-height)', }}>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h1" sx={{ color: 'primary.main', fontSize: { xs: 28, md: 34 } }}>
              Notifiche
            </Typography>
            <Box sx={{ height: 4, flex: 1, bgcolor: 'rgba(223,191,193,0.32)', borderRadius: 999 }} />
          </Box>

          {loading ? (
            <SurfaceCard>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>Caricamento notifiche...</Typography>
            </SurfaceCard>
          ) : visibleNotifications.length > 0 ? (
            <Stack spacing={1.5}>
              {visibleNotifications.map((notification, index) => (
                <NotificationCard
                  key={notification.id || index}
                  title={notification.content.title}
                  body={notification.content.body || notification.content.message || 'Apri per vedere i dettagli.'}
                  time={relativeTime(notification.createdAt)}
                  type={activeTab}
                  onClick={() => {
                    const destination = getNotificationDestination(notification);
                    if (destination) openNotificationDestination(navigate, destination);
                    else navigate('/notification', { state: { notification } });
                  }}
                />
              ))}
            </Stack>
          ) : (
            <SurfaceCard>
              <Typography variant="h3" sx={{ color: 'primary.main' }}>
                Nessuna notifica
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                Qui vedrai aggiornamenti sui tavoli, richieste e conferme.
              </Typography>
            </SurfaceCard>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Pagination count={totalPages} page={page} onChange={(event, value) => setPage(value)} color="primary" />
            </Box>
          )}
        </Stack>
      </PageContainer>
    </>
  );
}

function relativeTime(value) {
  const date = new Date(value);
  const diff = Date.now() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (Number.isNaN(diff)) return '';
  if (diff < minute) return 'ora';
  if (diff < hour) return `${Math.floor(diff / minute)}m fa`;
  if (diff < day) return `${Math.floor(diff / hour)}h fa`;
  return `${Math.floor(diff / day)}g fa`;
}
